/**
 * CFPreviewPill — silent "as-you-type" decode preview shown right below
 * the tax_code input in DealerCustomerForm.
 *
 * Behavior:
 *   1. As the dealer types the CF, this component debounces (400ms) and
 *      calls `POST /api/tools/decode-cf`.
 *   2. When the response arrives, shows a small info pill with the
 *      decoded identity ("Nato/a il 03/04/1985 a Torino (TO), M").
 *   3. If the seller has already typed first_name / last_name and the
 *      CF's surname/name triples do NOT match, shows a yellow warning
 *      badge below the info pill.
 *   4. If the CF is invalid (bad checksum, wrong length, unknown
 *      Belfiore), stays silent — the existing red-outline validation
 *      of the tax_code input already flags that case.
 *
 * Zero click, silent, informative. Feature Req. Fabrizio 27/07/2026
 * — "decode CF inverso as-you-type per feedback immediato di consistenza".
 */
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { UserCheck, AlertTriangle, Loader2 } from "lucide-react";
import { dealerApi } from "../../utils/dealerApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  IT: {
    born: "Nato/a il",
    at: "a",
    male: "M",
    female: "F",
    checking: "Verifica in corso...",
    lastnameMismatch: "Il cognome nel Codice Fiscale non combacia con",
    firstnameMismatch: "Il nome nel Codice Fiscale non combacia con",
    bothMismatch: "Nome e cognome nel Codice Fiscale non combaciano con i dati inseriti",
    genderLabel: { M: "Maschio", F: "Femmina" },
  },
  UK: {
    born: "Born on",
    at: "in",
    male: "M",
    female: "F",
    checking: "Checking...",
    lastnameMismatch: "The CF surname does not match",
    firstnameMismatch: "The CF first name does not match",
    bothMismatch: "First name and surname in the CF do not match the entered data",
    genderLabel: { M: "Male", F: "Female" },
  },
};

const getT = (lang) => T[(lang || "IT").toUpperCase()] || T.IT;

// Small, cheap CF-format sanity check to avoid firing HTTP for junk
// strings while the user is still typing. Real validation happens on
// the backend via codicefiscale.is_valid().
const looksLikeCF = (v) => /^[A-Z0-9]{16}$/.test((v || "").trim().toUpperCase());

/**
 * Format the decoded birthdate (ISO YYYY-MM-DD) as Italian DD/MM/YYYY.
 * Non-ISO falls through untouched.
 */
const formatItalianDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const CFPreviewPill = ({
  taxCode,
  firstName = "",
  lastName = "",
  dealerToken,
  lang = "IT",
}) => {
  const t = getT(lang);
  const [decoded, setDecoded] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    // Cancel any pending debounce + in-flight request when inputs change.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!looksLikeCF(taxCode) || !dealerToken) {
      setDecoded(null);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await dealerApi.post(
          `/tools/decode-cf`,
          { token: dealerToken, config: { signal: controller.signal } },
          {
            codice_fiscale: taxCode.trim().toUpperCase(),
            first_name: firstName || null,
            last_name: lastName || null,
          },
        );
        setDecoded(res.data);
      } catch (err) {
        // Silent on any failure (bad CF, wrong checksum, aborted, etc.)
        // — the tax_code input's own validation flag will surface it.
        if (!axios.isCancel?.(err) && err?.name !== "CanceledError") {
          setDecoded(null);
        }
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [taxCode, firstName, lastName, dealerToken]);

  if (!looksLikeCF(taxCode)) return null;
  if (loading) {
    return (
      <div
        className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500"
        data-testid="cf-preview-loading"
      >
        <Loader2 size={11} className="animate-spin" />
        <span>{t.checking}</span>
      </div>
    );
  }
  if (!decoded || !decoded.valid) return null;

  const dateFormatted = formatItalianDate(decoded.birth_date);
  const place = decoded.birth_place;
  const provOrCountry = decoded.is_foreign_birth
    ? "" // country name is already inside `place` for foreign births
    : decoded.birth_province
      ? ` (${decoded.birth_province})`
      : "";

  // Consistency check outcome (null = not applicable, seller hasn't
  // typed the name yet)
  const lnMismatch = decoded.lastname_matches === false;
  const fnMismatch = decoded.firstname_matches === false;
  const showMismatch = lnMismatch || fnMismatch;

  return (
    <div className="mt-1.5 space-y-1" data-testid="cf-preview-container">
      {/* Silent info pill */}
      <div
        className="flex items-start gap-1.5 text-[11px] text-gray-400 leading-snug"
        data-testid="cf-preview-info"
      >
        <UserCheck size={11} className="text-emerald-400 flex-shrink-0 mt-0.5" />
        <span>
          {t.born} <strong className="text-gray-300">{dateFormatted}</strong>{" "}
          {t.at}{" "}
          <strong className="text-gray-300">
            {place}
            {provOrCountry}
          </strong>
          , <strong className="text-gray-300">{t.genderLabel[decoded.gender] || decoded.gender}</strong>
        </span>
      </div>

      {/* Mismatch warning — yellow badge, non-blocking */}
      {showMismatch && (
        <div
          className="flex items-start gap-1.5 text-[11px] text-amber-300 leading-snug bg-amber-900/20 border border-amber-800/40 rounded-md px-2 py-1"
          data-testid="cf-preview-mismatch"
        >
          <AlertTriangle size={11} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <span>
            {lnMismatch && fnMismatch
              ? t.bothMismatch
              : lnMismatch
                ? `${t.lastnameMismatch} "${lastName}" (CF: ${decoded.cf_lastname_code})`
                : `${t.firstnameMismatch} "${firstName}" (CF: ${decoded.cf_firstname_code})`}
          </span>
        </div>
      )}
    </div>
  );
};

CFPreviewPill.propTypes = {
  taxCode: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  dealerToken: PropTypes.string.isRequired,
  lang: PropTypes.string,
};

export default CFPreviewPill;
